import { Button } from '@finn-auto/design-system/atoms/button';
import type { OutreachCandidate } from '../../types/taOutreach';

const BOX = 'flex flex-wrap items-center gap-2 rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-[13px] text-[#7A4B00]';

/**
 * What an open card says about who signs it (moved out of `OutreachCard`, Oct 28 2026):
 * - the viewer's OWN draft for another sender or caller (`sentAsStale`, Oct 26 2026): "Update"
 *   puts it together again (apply-template);
 * - written with an older version (`draftOutdated`, Oct 27 2026): "Update" = Redraft;
 * - a colleague's draft for another sender (`sentAsOther`, Sofia, Oct 28 2026): not held, one grey
 *   line — Approve or a send puts it together for the viewer first; when that could not be done
 *   (`why`), the reason and "Update" (= Redraft); a hand-edited one that was (`resigned` = the
 *   writer's first name): their edits are gone, one more look before Approve;
 * - else, when someone else takes the call: whose signature and whose booking link.
 */
export function SentAsNote({
  c,
  edited,
  why,
  resigned,
  disabled,
  onReassemble,
  onRedraft,
}: {
  c: OutreachCandidate;
  edited: boolean;
  why: string | null;
  /** A hand-edited draft put together as the viewer: whose edits were replaced (Sofia, Oct 28 2026). */
  resigned: string | null;
  disabled: boolean;
  onReassemble: () => void;
  onRedraft: () => void;
}) {
  const s = c.draft?.sentAs;
  const update = (onClick: () => void) => (
    <Button size="sm" variant="outline" className="rounded-md" disabled={disabled} onClick={onClick}>
      Update
    </Button>
  );
  return (
    <>
      {c.sentAsStale && (
        <div className={BOX}>
          <span>
            Written as {s?.senderName || 'you'}
            {s && s.interviewer !== s.sendAs ? `, call with ${s.interviewerName}` : ''} — not who you picked above. Update it before sending
            {c.draft?.source === 'edited' ? '; your edits to this text are replaced' : ''}.
          </span>
          {update(onReassemble)}
        </div>
      )}
      {c.draftOutdated && !c.sentAsStale && (
        <div className={BOX} data-testid="draft-outdated">
          <span>Written with an older version</span>
          {update(onRedraft)}
        </div>
      )}
      {why && (
        <div className={BOX} data-testid="not-signed">
          <span>Can’t be put together as you: {why}.</span>
          {update(onRedraft)}
        </div>
      )}
      {resigned && !why && (
        <p className="m-0 text-xs text-[#707070]" data-testid="resigned">
          {resigned}’s edits were replaced when it was put together as you — check the text and approve again.
        </p>
      )}
      {c.sentAsOther && !why && (
        <p className="m-0 text-xs text-[#707070]" data-testid="sent-as-other">
          Signed by {s?.senderName || 'a colleague'} · Approve or send puts it together as you{edited ? '; edits are replaced' : ''}.
        </p>
      )}
      {!c.sentAsStale && !c.sentAsOther && s && s.interviewer !== s.sendAs && (
        <p className="m-0 text-xs text-[#707070]">
          Signed by {s.senderName} · the booking link is {s.interviewerName}’s.
        </p>
      )}
    </>
  );
}
